import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  CheckboxGrid,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getRnVisitNoteForm,
  updateRnVisitNoteForm,
} from "../../../../../services/patientService";
import {
  CommentsIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HelpingIcon,
  NurseIcon,
  PhoneIcon,
  PillIcon,
  ShieldCheckIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { PATIENT_THEME } from "../../../patientTheme";
import {
  buildDiagnosisAllergies,
  buildAllergies,
  formatAllergyNames,
  buildPhysicians,
} from "../../../patientForm.shared";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  RN_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import { USER_ROLES } from "../../../../../constants";
import {
  CAREGIVER_PARTICIPATION_OPTIONS,
  COMMUNICATION_METHOD_OPTIONS,
  GI_GU_OPTIONS,
  MEDS_COMPLIANCE_OPTIONS,
  PROGRESS_STATUS_OPTIONS,
  REASON_FOCUS_OPTIONS,
  YES_NO_OPTIONS,
  Y_N_OPTIONS,
  emptyRnVisitPayload,
  toggleInList,
} from "./rnVisitNoteContent";
import {
  assertNurseVisitMetaForFlag,
  buildNurseVisitMetaProps,
  pickNurseVisitMeta,
} from "./nurseVisitCommon";
import NurseNotesCommonHeader from "./NurseNotesCommonHeader";
import ClinicalVitalsFields from "./ClinicalVitalsFields";
import WoundPresentSection from "./WoundPresentSection";
import { applyWoundAssessmentPayload } from "./woundAssessmentContent";
import {
  normalizeClinicalVitals,
  withCanonicalClinicalVitals,
} from "./clinicalVitals";
import {
  normalizeCommonHeaderState,
  resolveNurseNotesCommonHeader,
  withNormalizedCommonHeader,
} from "./nurseNotesCommonHeaderUtils";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const ARRAY_PAYLOAD_KEYS = ["disciplines", "reasonFocus"];

const emptyValues = () => ({
  ...emptyRnVisitPayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const PillRadioGroup = ({
  label,
  name,
  options,
  value,
  canEdit,
  onChange,
  compact = false,
}) => (
  <div>
    {label ? (
      <SubHeading>{label}</SubHeading>
    ) : null}
    <div
      className="flex flex-wrap gap-1.5"
      role="radiogroup"
      aria-label={label || name}
    >
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={!canEdit}
            onClick={() => onChange(active ? "" : opt)}
            className={`${compact ? "h-7 px-2 text-[10px]" : "h-8 px-2.5 text-[11px]"} rounded font-medium border transition-colors disabled:opacity-60 whitespace-nowrap ${
              active
                ? "bg-indigo-600 text-white border-indigo-600"
                : "bg-indigo-50/60 text-indigo-800 border-indigo-100 hover:bg-indigo-100"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

PillRadioGroup.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  compact: PropTypes.bool,
};

const RnVisitNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const normalizeLoadedPayload = useCallback((loadedPayload) => {
    const base = emptyRnVisitPayload();
    const merged = { ...base, ...loadedPayload };

    ARRAY_PAYLOAD_KEYS.forEach((key) => {
      merged[key] = Array.isArray(loadedPayload[key]) ? loadedPayload[key] : [];
    });

    merged.goalsReviewed = Boolean(loadedPayload.goalsReviewed);
    merged.planUpdated = Boolean(loadedPayload.planUpdated);
    merged.caregiverPresent = Boolean(loadedPayload.caregiverPresent);
    merged.commonHeader = normalizeCommonHeaderState(loadedPayload.commonHeader);

    if (!merged.visitType) merged.visitType = "Follow Up";
    if (!merged.serviceLocation) merged.serviceLocation = "Patient Home";

    Object.assign(merged, normalizeClinicalVitals(merged));
    applyWoundAssessmentPayload(merged, merged, {
      mrn: patient?.mrn,
    });
    delete merged.vitalsBpSys;
    delete merged.vitalsBpDia;
    delete merged.vitalsPain;

    return merged;
  }, [patient?.mrn]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      setValues({
        ...normalizeLoadedPayload(loadedPayload),
        ...pickNurseVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [normalizeLoadedPayload, patientName, resetSignatures],
  );

  /** Visit-specific chart fields (common header covers demographics / dx / meds). */
  const buildPatientPrefill = useCallback(() => {
    const physicians = buildPhysicians(patient);
    const primaryPhys = physicians[0];
    const diagnoses = buildDiagnosisAllergies(patient);
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];
    const soc = latestEpisode?.socDate || latestEpisode?.startDate || "";
    const certTo = latestEpisode?.endDate || latestEpisode?.toDate || "";
    const allergyRows = buildAllergies(patient);
    const allergies = formatAllergyNames(
      allergyRows.length
        ? allergyRows
        : patient?.diagnosisAllergies || diagnoses,
    );
    const providerName =
      role === USER_ROLES.RN || role === USER_ROLES.LVN
        ? [currentUser?.lastName, currentUser?.firstName]
            .filter(Boolean)
            .join(", ")
        : "";

    return {
      ...emptyRnVisitPayload(),
      mrn: patient?.mrn || "",
      primaryLanguage: patient?.language || "",
      allergies,
      visitType: "Follow Up",
      serviceLocation: "Patient Home",
      certPeriodFrom: soc ? formatDateForInput(soc) : "",
      certPeriodTo: certTo ? formatDateForInput(certTo) : "",
      providerRn: providerName,
      physicianNotifiedName: primaryPhys?.physicianName || "",
    };
  }, [currentUser?.firstName, currentUser?.lastName, patient, role]);

  const mergePatientSnapshot = useCallback(
    (existingPayload) => {
      const prefill = buildPatientPrefill();
      return {
        ...prefill,
        ...existingPayload,
        mrn: existingPayload.mrn || prefill.mrn || patient?.mrn || "",
        primaryLanguage:
          existingPayload.primaryLanguage || prefill.primaryLanguage || "",
        allergies: existingPayload.allergies || prefill.allergies || "",
        certPeriodFrom:
          existingPayload.certPeriodFrom || prefill.certPeriodFrom || "",
        certPeriodTo:
          existingPayload.certPeriodTo || prefill.certPeriodTo || "",
        providerRn: existingPayload.providerRn || prefill.providerRn || "",
        physicianNotifiedName:
          existingPayload.physicianNotifiedName ||
          prefill.physicianNotifiedName ||
          "",
      };
    },
    [buildPatientPrefill, patient],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveNurseNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName || draft?.formName || "Nurse Visit Note",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getRnVisitNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveNurseNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...mergePatientSnapshot(existingPayload), commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // Load once per form identity. Prefill helpers + patient chart are read
    // from the render that starts the request; including them in deps caused
    // an infinite refetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        RN_FORM_EDITOR_ROLES,
        "View only — only RN/LVN can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      visitDate,
      ...rnFields
    } = values;

    const nestedPayload = withCanonicalClinicalVitals(
      withNormalizedCommonHeader({
        ...rnFields,
        dateOfBirth: values.dateOfBirth
          ? formatDateForInput(values.dateOfBirth)
          : "",
        certPeriodFrom: values.certPeriodFrom
          ? formatDateForInput(values.certPeriodFrom)
          : "",
        certPeriodTo: values.certPeriodTo
          ? formatDateForInput(values.certPeriodTo)
          : "",
        visitDate: visitDate ? formatDateForInput(visitDate) : "",
        disciplines: values.disciplines || [],
        reasonFocus: values.reasonFocus || [],
        incisionClosedWith: values.incisionClosedWith || [],
        woundMapSites: values.woundMapSites || [],
        wounds: Array.isArray(values.wounds) ? values.wounds : [],
        goalsReviewed: Boolean(values.goalsReviewed),
        planUpdated: Boolean(values.planUpdated),
        caregiverPresent: Boolean(values.caregiverPresent),
        commonHeader: values.commonHeader,
      }),
    );

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertNurseVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateRnVisitNoteForm,
        filledByUserId,
        scheduleDate: values.visitDate
          ? formatDateForInput(values.visitDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Nurse Follow Up Visit
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home health agency visit documentation.
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn || values.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              values.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Date"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Date
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildNurseVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <NurseNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />



        <Section icon={CommentsIcon} title="7. Reason for Visit / Primary Focus">
          <div className="mb-3">
            <SubHeading>Primary Focus</SubHeading>
            <CheckboxGrid
              options={REASON_FOCUS_OPTIONS}
              values={values.reasonFocus}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("reasonFocus", opt)}
              columns={2}
            />
          </div>
          <Field label="Reason / Description">
            <textarea
              rows={3}
              className={textareaClass}
              value={values.reasonDescription}
              disabled={!canEdit}
              placeholder="Describe the reason for today's visit and clinical focus…"
              onChange={(e) => setValue("reasonDescription", e.target.value)}
            />
          </Field>
        </Section>

        <Section icon={HeartPulseIcon} title="8. Clinical Vitals & Trends">
          <ClinicalVitalsFields
            values={values}
            canEdit={canEdit}
            onChange={(key, next) => setValue(key, next)}
          />
        </Section>

        <Section icon={HeartPulseIcon} title="9. Assessment">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-3">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                General Assessment
              </div>
              <PillRadioGroup
                label="Alert & Oriented"
                name="alertOriented"
                options={YES_NO_OPTIONS}
                value={values.alertOriented}
                canEdit={canEdit}
                onChange={(v) => setValue("alertOriented", v)}
                compact
              />
              <PillRadioGroup
                label="Skin Warm / Dry"
                name="skinWarmDry"
                options={YES_NO_OPTIONS}
                value={values.skinWarmDry}
                canEdit={canEdit}
                onChange={(v) => setValue("skinWarmDry", v)}
                compact
              />
              <PillRadioGroup
                label="Lungs Clear"
                name="lungsClear"
                options={YES_NO_OPTIONS}
                value={values.lungsClear}
                canEdit={canEdit}
                onChange={(v) => setValue("lungsClear", v)}
                compact
              />
              <PillRadioGroup
                label="Edema"
                name="edema"
                options={YES_NO_OPTIONS}
                value={values.edema}
                canEdit={canEdit}
                onChange={(v) => setValue("edema", v)}
                compact
              />
              <PillRadioGroup
                label="GI / GU"
                name="giGu"
                options={GI_GU_OPTIONS}
                value={values.giGu}
                canEdit={canEdit}
                onChange={(v) => setValue("giGu", v)}
                compact
              />
              {values.giGu === "Abnormal" && (
                <Field label="GI / GU Details">
                  <textarea
                    rows={2}
                    className={textareaClass}
                    value={values.giGuDetails}
                    disabled={!canEdit}
                    onChange={(e) => setValue("giGuDetails", e.target.value)}
                  />
                </Field>
              )}
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-2.5 space-y-3">
              <div
                className={`text-[9px] font-semibold uppercase tracking-wide mb-1 ${PATIENT_THEME.section.title}`}
              >
                System Assessment
              </div>
              <Field label="Cardiovascular">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.systemCardiovascular}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("systemCardiovascular", e.target.value)
                  }
                />
              </Field>
              <Field label="Respiratory">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.systemRespiratory}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("systemRespiratory", e.target.value)
                  }
                />
              </Field>
              <Field label="Neurological">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.systemNeurological}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("systemNeurological", e.target.value)
                  }
                />
              </Field>
              <Field label="Integumentary (Skin)">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.systemIntegumentary}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("systemIntegumentary", e.target.value)
                  }
                />
              </Field>
            </div>
          </div>
        </Section>

        <Section icon={PillIcon} title="10. Medications">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <PillRadioGroup
              label="Reviewed"
              name="medsReviewed"
              options={YES_NO_OPTIONS}
              value={values.medsReviewed}
              canEdit={canEdit}
              onChange={(v) => setValue("medsReviewed", v)}
            />
            <PillRadioGroup
              label="Changes"
              name="medsChanges"
              options={YES_NO_OPTIONS}
              value={values.medsChanges}
              canEdit={canEdit}
              onChange={(v) => setValue("medsChanges", v)}
            />
            <PillRadioGroup
              label="Compliance"
              name="medsCompliance"
              options={MEDS_COMPLIANCE_OPTIONS}
              value={values.medsCompliance}
              canEdit={canEdit}
              onChange={(v) => setValue("medsCompliance", v)}
            />
          </div>
          <div className="mt-3">
            <Field label="Issues / Notes">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.medsNotes}
                disabled={!canEdit}
                placeholder="Reconciliation notes, changes reported, education provided…"
                onChange={(e) => setValue("medsNotes", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={NurseIcon} title="11. Wound Assessment (If Applicable)">
          <WoundPresentSection
            values={values}
            canEdit={canEdit}
            printMode={printMode}
            onChange={(key, next) => setValue(key, next)}
            onToggleList={(key, opt) => toggleListValue(key, opt)}
          />
        </Section>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Section icon={ShieldCheckIcon} title="12. Safety">
            <div className="space-y-3">
              <PillRadioGroup
                label="Fall Risk"
                name="fallRisk"
                options={Y_N_OPTIONS}
                value={values.fallRisk}
                canEdit={canEdit}
                onChange={(v) => setValue("fallRisk", v)}
                compact
              />
              <PillRadioGroup
                label="Home Hazards"
                name="homeHazards"
                options={Y_N_OPTIONS}
                value={values.homeHazards}
                canEdit={canEdit}
                onChange={(v) => setValue("homeHazards", v)}
                compact
              />
              <Field label="Assistive Devices">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.assistiveDevicesText}
                  disabled={!canEdit}
                  placeholder="Cane, Walker, etc."
                  onChange={(e) =>
                    setValue("assistiveDevicesText", e.target.value)
                  }
                />
              </Field>
            </div>
          </Section>

          <Section icon={TargetIcon} title="13. Progress">
            <div className="space-y-3">
              <label
                className={`flex items-center gap-2 text-[12px] rounded-md border px-2.5 py-2 ${
                  canEdit ? "cursor-pointer" : "cursor-default"
                } ${
                  values.goalsReviewed
                    ? "border-indigo-300 bg-indigo-50/60"
                    : "border-slate-200 bg-white"
                }`}
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={values.goalsReviewed}
                  disabled={!canEdit}
                  onChange={(e) => setValue("goalsReviewed", e.target.checked)}
                />
                <span className="font-medium text-slate-700">Goals Reviewed? Yes</span>
              </label>
              <PillRadioGroup
                label="Status"
                name="progressStatus"
                options={PROGRESS_STATUS_OPTIONS}
                value={values.progressStatus}
                canEdit={canEdit}
                onChange={(v) => setValue("progressStatus", v)}
                compact
              />
              <Field label="Comments">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.progressComments}
                  disabled={!canEdit}
                  onChange={(e) => setValue("progressComments", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          <Section icon={FileCheckIcon} title="14. Plan / Interventions">
            <div className="space-y-3">
              <label
                className={`flex items-center gap-2 text-[12px] rounded-md border px-2.5 py-2 ${
                  canEdit ? "cursor-pointer" : "cursor-default"
                } ${
                  values.planUpdated
                    ? "border-indigo-300 bg-indigo-50/60"
                    : "border-slate-200 bg-white"
                }`}
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={values.planUpdated}
                  disabled={!canEdit}
                  onChange={(e) => setValue("planUpdated", e.target.checked)}
                />
                <span className="font-medium text-slate-700">Plan Updated? Yes</span>
              </label>
              <Field label="Next Steps / Plan">
                <textarea
                  rows={4}
                  className={textareaClass}
                  value={values.nextStepsPlan}
                  disabled={!canEdit}
                  placeholder="Interventions, follow-up, coordination…"
                  onChange={(e) => setValue("nextStepsPlan", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          <Section icon={HelpingIcon} title="15. Caregiver">
            <div className="space-y-3">
              <label
                className={`flex items-center gap-2 text-[12px] rounded-md border px-2.5 py-2 ${
                  canEdit ? "cursor-pointer" : "cursor-default"
                } ${
                  values.caregiverPresent
                    ? "border-indigo-300 bg-indigo-50/60"
                    : "border-slate-200 bg-white"
                }`}
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={values.caregiverPresent}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("caregiverPresent", e.target.checked)
                  }
                />
                <span className="font-medium text-slate-700">
                  Caregiver Present? Yes
                </span>
              </label>
              <Field label="Participation">
                <select
                  className={fieldClass}
                  value={values.caregiverParticipation}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("caregiverParticipation", e.target.value)
                  }
                >
                  <option value="">Select…</option>
                  {CAREGIVER_PARTICIPATION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Education Provided">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.caregiverEducation}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("caregiverEducation", e.target.value)
                  }
                />
              </Field>
            </div>
          </Section>
        </div>

        <Section icon={PhoneIcon} title="16. Communication">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <PillRadioGroup
              label="Physician Notified?"
              name="physicianNotified"
              options={YES_NO_OPTIONS}
              value={values.physicianNotified}
              canEdit={canEdit}
              onChange={(v) => setValue("physicianNotified", v)}
            />
            <Field label="If Yes, Name">
              <input
                type="text"
                className={fieldClass}
                value={values.physicianNotifiedName}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("physicianNotifiedName", e.target.value)
                }
              />
            </Field>
            <Field label="Method">
              <select
                className={fieldClass}
                value={values.communicationMethod}
                disabled={!canEdit}
                onChange={(e) => setValue("communicationMethod", e.target.value)}
              >
                <option value="">Select…</option>
                {COMMUNICATION_METHOD_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="New Orders Received?">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.newOrdersReceived}
                  disabled={!canEdit}
                  placeholder="Detail any new orders..."
                  onChange={(e) => setValue("newOrdersReceived", e.target.value)}
                />
              </Field>
            </div>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection
          patientNameValue={values.patientSignatureName}
          onPatientNameChange={(v) => setValue("patientSignatureName", v)}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

RnVisitNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default RnVisitNoteForm;
